import { format, isSameYear } from 'date-fns';
import { getBlocksOfficeSession, getModeOfSale } from '../utils/cookies';
import { privateGet, publicGet } from '../utils/m2';
import { getSessionKey } from './user';
import zonedDate from '../utils/zoned-date';
import { getPackages } from './package';

let _digitalConcertsCache = {};
let _purchasesCache = null;
let _digitalCalendarPerformancesCache = null;
let _digitalEventDataCache = null;

const now = zonedDate();

function loadDigitalConcerts(pageID = 0) {
    const cacheKey = `page_id_${pageID}`;
    if (_digitalConcertsCache[cacheKey]) {
        return _digitalConcertsCache[cacheKey];
    }

    const params = {
        urlParams: {
            modeOfSale: getModeOfSale(),
            stage: window.AppConfig.stage.toLowerCase(),
            pageID: String(pageID),
        },
    }

    _digitalConcertsCache[cacheKey] = Promise.all([getPackages(), publicGet('digital-feed/{modeOfSale}/{stage}?pageID={pageID}', params)]).then(([packagesResponse, digitalResponse]) => {
        return digitalResponse.data.map(item => {
            item.stream_panel = {
                streaming_on: null,
                off_sale_date: null,
                available_until: null,
            }

            item.purchase_links = {
                performance: null,
                subscription: null,
            }

            if (item.is_paid) {
                if (item.on_sale_date && item.off_sale_date) {
                    const onSaleDate = zonedDate(item.on_sale_date);
                    const offSaleDate = zonedDate(item.off_sale_date);

                    if (now > onSaleDate && now < offSaleDate) {
                        const performanceDate = zonedDate(item.first_aired);

                        item.stream_panel.streaming_on = {
                            date: isSameYear(now, performanceDate) ? format(performanceDate, 'EEE MMM d') : format(performanceDate, 'EEE MMM d yyyy'),
                            time: format(performanceDate, 'h:mm aaa'),
                        }

                        if (item.perf_no) {
                            item.purchase_links.performance = {
                                title: 'Stream Now',
                                link: `${window.AppConfig.performanceBookingBaseUrl}/${item.perf_no}`,
                                price: item.price ? `$${item.price.toFixed(2)}` : null,
                            }
                        }
                    }
                }

                const subscription = packagesResponse.find(sub => {
                    return sub.Performances.some(performance => {
                        return parseInt(performance.Id, 10) === parseInt(item.perf_no, 10);
                    });
                });

                if (subscription && subscription.StartSaleDate && subscription.EndSaleDate) {
                    const startSaleDate = zonedDate(subscription.StartSaleDate);
                    const endSaleDate = zonedDate(subscription.EndSaleDate);

                    if (now > startSaleDate && now < endSaleDate) {
                        item.purchase_links.subscription = {
                            title: subscription.Title,
                            link: subscription.PackageLink,
                            price: subscription.Price,
                            prompt: subscription.Prompt,
                        }
                    }
                }
            }

            if (item.on_demand_end_date) {
                const availableUntilDate = zonedDate(item.on_demand_end_date);

                item.stream_panel.available_until = {
                    date: isSameYear(now, availableUntilDate) ? format(availableUntilDate, 'EEE MMM d') : format(availableUntilDate, 'EEE MMM d yyyy'),
                    time: format(availableUntilDate, 'h:mm aaa'),
                }
            }

            if (item.custom_off_sale_date) {
                const offSaleDate = zonedDate(item.custom_off_sale_date);

                item.stream_panel.off_sale_date = {
                    date: isSameYear(now, offSaleDate) ? format(offSaleDate, 'EEE MMM d') : format(offSaleDate, 'EEE MMM d yyyy'),
                    time: format(offSaleDate, 'h:mm aaa'),
                }
            }

            return item;
        });
    }).catch(error => {
        console.error(error);
        return [];
    });

    return _digitalConcertsCache[cacheKey];
}

function loadDigitalPurchases() {
    if (_purchasesCache) {
        return _purchasesCache;
    }

    _purchasesCache = getSessionKey().then(sessionKey => {
        if (sessionKey) {
            const params = {
                urlParams: {
                    sessionKey: getBlocksOfficeSession(),
                    stage: window.AppConfig.stage.toLowerCase(),
                },
            }

            return privateGet('digital-purchases/{sessionKey}/{stage}', params).then(response => {
                return response.data;
            });
        }

        return [];
    }).catch(error => {
        console.error(error);
        return [];
    });

    return _purchasesCache;
}

export function getAllDigitalConcerts(pageID = 0) {
    return loadDigitalPurchases().then(purchasesResponse => {
        return loadDigitalConcerts(pageID).then(digitalConcertsResponse => {
            const concerts = [];

            for (const concert of digitalConcertsResponse) {
                const purchase = purchasesResponse.find(item => {
                    return parseInt(item.perf_no, 10) === parseInt(concert.perf_no, 10);
                });

                if (purchase) {
                    concerts.push({
                        ...concert,
                        ...purchase,
                    });
                } else {
                    concerts.push(concert);
                }
            }

            return concerts;
        });
    });
}

export function getPurchasedDigitalConcerts() {
    return loadDigitalPurchases().then(purchasesResponse => {
        return loadDigitalConcerts().then(digitalConcertsResponse => {
            const purchases = [];

            for (const purchase of purchasesResponse) {
                const digitalConcert = digitalConcertsResponse.find(item => {
                    return parseInt(item.perf_no, 10) === parseInt(purchase.perf_no, 10);
                });

                if (!digitalConcert) {
                    continue;
                }

                purchases.push({
                    ...digitalConcert,
                    ...purchase,
                });
            }

            return purchases;
        });
    });
}

export function getDigitalPerformances(digitalTypeFilter = null) {
    if (_digitalCalendarPerformancesCache) {
        return _digitalCalendarPerformancesCache;
    }

    _digitalCalendarPerformancesCache = loadDigitalPurchases().then(purchasesResponse => {
        return loadDigitalConcerts().then(data => {
            if (digitalTypeFilter) {
                data = data.filter(item => item.type === digitalTypeFilter)
            }

            return data.map(perf => {
                const purchase = purchasesResponse.find(item => {
                    return parseInt(item.perf_no, 10) === parseInt(perf.perf_no, 10);
                });

                perf.ID = perf.perf_no ? perf.perf_no : perf.id;
                perf.Image = perf.image;
                perf.PerformanceDate = zonedDate(perf.first_aired);
                perf.Day =  perf.PerformanceDate ? format(perf.PerformanceDate, 'EEE') : null;
                perf.Date = perf.PerformanceDate ? format(perf.PerformanceDate, 'd') : null;
                perf.Month = perf.PerformanceDate ? format(perf.PerformanceDate, 'MMM') : null;
                perf.Prefix = perf.prefix;
                perf.Title = perf.title;
                perf.Suffix = perf.suffix;
                perf.Details = perf.details;
                perf.Duration = perf.duration;
                perf.Time = perf.PerformanceDate ? format(perf.PerformanceDate, 'h:mm aaa') : null;
                perf.Category = perf.performance_type;
                perf.ShowInArchiveFilter = perf.show_in_archive_filter;
                perf.IsPurchased = !!(purchase || perf.video_id || perf.type === 'audio');
                perf.StreamTime = format(perf.PerformanceDate, 'MMM d, yyyy');

                if (perf.perf_no) {
                    perf.TicketLink = `${window.AppConfig.performanceBookingBaseUrl}/${perf.perf_no}`;
                } else {
                    perf.TicketLink = perf.url;
                }

                if (perf.type === 'audio') {
                    perf.Type = 'Audio';
                } else {
                    perf.Type = 'Video';
                }

                perf.Filters = {
                    Type: []
                }

                if (perf.Category) {
                    perf.Filters.Type.push(perf.Category);
                }

                perf.StreamingOn = perf.stream_panel.streaming_on;
                perf.AvailableUntil = perf.stream_panel.available_until;
                perf.BuyPerformance = perf.purchase_links.performance;
                perf.BuySubscription = perf.purchase_links.subscription;

                return perf;
            }).sort((a, b) => {
                return a.PerformanceDate - b.PerformanceDate;
            });
        });
    });

    return _digitalCalendarPerformancesCache;
}

export function getDigitalEventData(digitalTypeFilter = null) {
    if (_digitalEventDataCache) {
        return _digitalEventDataCache;
    }

    _digitalEventDataCache = getDigitalPerformances(digitalTypeFilter).then(perfs => {
        perfs = perfs.filter(perf => {
            const firstAiredDate = perf.first_aired ? zonedDate(perf.first_aired) : null;
            const onDemandEndDate = perf.on_demand_end_date ? zonedDate(perf.on_demand_end_date) : null;

            // If the performance has a first aired date and it's in the future, or if it has an on demand end date and it's in the future, then show it
            if ((firstAiredDate && firstAiredDate > now) || (onDemandEndDate && onDemandEndDate > now)) {
                return true;
            } else if ((firstAiredDate && firstAiredDate <= now) && !onDemandEndDate) {
                return false;
            } else {
                return false;
            }
        });

        return perfs.reduce((carry, perf) => {
            const year = format(perf.PerformanceDate, 'yyyy');
            const monthName = format(perf.PerformanceDate, 'MMMM');
            const month = format(perf.PerformanceDate, 'M');

            let yearData = carry.find(item => {
                return item.Name === year;
            });

            if (!yearData) {
                yearData = {
                    Name: year,
                    Order: parseInt(year, 10),
                    Months: [],
                };

                carry.push(yearData)
            }

            let monthData = yearData.Months.find(item => {
                return item.Name === monthName;
            });

            if (!monthData) {
                monthData = {
                    Name: monthName,
                    Order: parseInt(month, 10),
                    Performances: [],
                }

                yearData.Months.push(monthData);
            }

            monthData.Performances.push(perf);

            return carry;
        }, []);
    });

    return _digitalEventDataCache;
}
